
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { RefreshCw, TrendingDown, TrendingUp, BarChart3 } from "lucide-react";
import { useSupabase } from "@/hooks/useSupabase";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface OrcamentoAgregado {
  ano: number;
  centro_resultado: string;
  tipo_receita_despesa: string;
  classificacao_receita_despesa: string;
  valor_total_orcado: number;
  quantidade_registros: number;
  valor_minimo: number;
  valor_maximo: number;
  valor_medio: number;
}

export default function AnaliseOrcamentoPage() {
  const { supabase } = useSupabase();
  const [data, setData] = useState<OrcamentoAgregado[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtroAno, setFiltroAno] = useState<string>("");
  const [filtroCentro, setFiltroCentro] = useState<string>("");
  const [filtroTipo, setFiltroTipo] = useState<string>("");
  const [anos, setAnos] = useState<number[]>([]);
  const [centros, setCentros] = useState<string[]>([]);
  const [tipos, setTipos] = useState<string[]>([]);

  useEffect(() => {
    fetchData();
    fetchFiltros();
  }, []);

  const fetchFiltros = async () => {
    try {
      const { data: filtrosData, error } = await supabase
        .from("solis_vw_orcamento_por_tipo_centro")
        .select("ano, centro_resultado, tipo_receita_despesa");

      if (error) throw error;

      // Extrair valores únicos para os filtros
      const anosUnicos = [...new Set(filtrosData?.map((d: any) => d.ano) || [])].sort((a, b) => b - a) as number[];
      const centrosUnicos = [...new Set(filtrosData?.map((d: any) => d.centro_resultado).filter(Boolean) || [])].sort() as string[];
      const tiposUnicos = [...new Set(filtrosData?.map((d: any) => d.tipo_receita_despesa).filter(Boolean) || [])].sort() as string[];

      setAnos(anosUnicos);
      setCentros(centrosUnicos);
      setTipos(tiposUnicos);
    } catch (error: any) {
      console.error("Erro ao buscar filtros:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("solis_vw_orcamento_por_tipo_centro")
        .select("*")
        .order("ano", { ascending: false })
        .order("centro_resultado")
        .order("tipo_receita_despesa");

      // Aplicar filtros
      if (filtroAno) {
        query = query.eq("ano", parseInt(filtroAno));
      }
      if (filtroCentro) {
        query = query.eq("centro_resultado", filtroCentro);
      }
      if (filtroTipo) {
        query = query.eq("tipo_receita_despesa", filtroTipo);
      }

      const { data: viewData, error } = await query;

      if (error) throw error;
      setData(viewData || []);
    } catch (error: any) {
      console.error("Erro ao buscar dados:", error);
      toast.error("Erro ao carregar análise do orçamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filtroAno, filtroCentro, filtroTipo]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  // Calcular totais
  const totalGeral = data.reduce((sum, item) => sum + Number(item.valor_total_orcado || 0), 0);
  const totalRegistros = data.reduce((sum, item) => sum + Number(item.quantidade_registros || 0), 0);

  // Agrupar por centro de custo
  const porCentro = data.reduce((acc, item) => {
    const centro = item.centro_resultado || "Sem centro";
    if (!acc[centro]) {
      acc[centro] = 0;
    }
    acc[centro] += Number(item.valor_total_orcado || 0);
    return acc;
  }, {} as Record<string, number>);

  // Agrupar por tipo de despesa
  const porTipo = data.reduce((acc, item) => {
    const tipo = item.tipo_receita_despesa || "Sem tipo";
    if (!acc[tipo]) {
      acc[tipo] = 0;
    }
    acc[tipo] += Number(item.valor_total_orcado || 0);
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-4 p-6 max-w-7xl w-full mx-auto">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Análise de Orçamento</h2>
          <p className="text-muted-foreground text-sm">
            Visualize o orçamento agregado por ano, tipo de despesa e centro de custo.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchData}
          disabled={loading}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Ano</label>
              <Select value={filtroAno || "__all__"} onValueChange={(val) => setFiltroAno(val === "__all__" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os anos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos os anos</SelectItem>
                  {anos.map((ano) => (
                    <SelectItem key={ano} value={String(ano)}>
                      {ano}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Centro de Custo</label>
              <Select value={filtroCentro || "__all__"} onValueChange={(val) => setFiltroCentro(val === "__all__" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os centros" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos os centros</SelectItem>
                  {centros.map((centro) => (
                    <SelectItem key={centro} value={centro}>
                      {centro}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Tipo de Despesa</label>
              <Select value={filtroTipo || "__all__"} onValueChange={(val) => setFiltroTipo(val === "__all__" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos os tipos</SelectItem>
                  {tipos.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>
                      {tipo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards de resumo */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orçado</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${totalGeral < 0 ? "text-red-600" : "text-green-600"}`}>
              {formatCurrency(totalGeral)}
            </div>
            <p className="text-xs text-muted-foreground">
              Soma de todos os valores filtrados
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Registros</CardTitle>
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalRegistros}</div>
            <p className="text-xs text-muted-foreground">
              Quantidade de registros agregados
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Grupos</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.length}</div>
            <p className="text-xs text-muted-foreground">
              Combinações únicas (ano/tipo/centro)
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Resumo por Centro de Custo */}
      {Object.keys(porCentro).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Resumo por Centro de Custo</CardTitle>
            <CardDescription>Total orçado por centro de custo</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(porCentro)
                .sort(([, a], [, b]) => a - b)
                .map(([centro, valor]) => (
                  <div key={centro} className="flex items-center justify-between p-2 rounded border">
                    <span className="font-medium">{centro}</span>
                    <span className={valor < 0 ? "text-red-600 font-bold" : "text-green-600 font-bold"}>
                      {formatCurrency(valor)}
                    </span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resumo por Tipo de Despesa */}
      {Object.keys(porTipo).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Resumo por Tipo de Despesa</CardTitle>
            <CardDescription>Total orçado por tipo de despesa</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(porTipo)
                .sort(([, a], [, b]) => a - b)
                .map(([tipo, valor]) => (
                  <div key={tipo} className="flex items-center justify-between p-2 rounded border">
                    <span className="font-medium">{tipo}</span>
                    <span className={valor < 0 ? "text-red-600 font-bold" : "text-green-600 font-bold"}>
                      {formatCurrency(valor)}
                    </span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabela detalhada */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhamento</CardTitle>
          <CardDescription>
            Orçamento agregado por ano, tipo de despesa e centro de custo
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : data.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum dado encontrado com os filtros selecionados.
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ano</TableHead>
                    <TableHead>Centro de Custo</TableHead>
                    <TableHead>Tipo de Despesa</TableHead>
                    <TableHead>Classificação</TableHead>
                    <TableHead className="text-right">Valor Total</TableHead>
                    <TableHead className="text-right">Qtd. Registros</TableHead>
                    <TableHead className="text-right">Mínimo</TableHead>
                    <TableHead className="text-right">Máximo</TableHead>
                    <TableHead className="text-right">Média</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{item.ano}</TableCell>
                      <TableCell>{item.centro_resultado || "-"}</TableCell>
                      <TableCell>{item.tipo_receita_despesa || "-"}</TableCell>
                      <TableCell>{item.classificacao_receita_despesa || "-"}</TableCell>
                      <TableCell className={`text-right font-bold ${Number(item.valor_total_orcado) < 0 ? "text-red-600" : "text-green-600"}`}>
                        {formatCurrency(Number(item.valor_total_orcado))}
                      </TableCell>
                      <TableCell className="text-right">{item.quantidade_registros}</TableCell>
                      <TableCell className="text-right">{formatCurrency(Number(item.valor_minimo))}</TableCell>
                      <TableCell className="text-right">{formatCurrency(Number(item.valor_maximo))}</TableCell>
                      <TableCell className="text-right">{formatCurrency(Number(item.valor_medio))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

